import { useTranslation } from "react-i18next";
import { Icons, Typography } from "@reconvert/react-ui-component";
import {
  Box,
  Modal,
  Divider,
  Flex,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useAppSelector } from "../../app/hooks";
import { selectAppState } from "../../app/slice/appSlice";
import { PageType } from "@reconvert/reconvert-utils";
import { useToast } from "@reconvert/react-ui-component";
import {
  PageNames,
  ShopifyCheckoutEditorPage,
  ShopifyCheckoutEditorPageCollection,
} from "../../app/slice/appSlice.model";

//@ts-ignore
import ThankYouPageGif from "../../assets/gif/ThankYouPage.gif";
//@ts-ignore
import OrderStatusPageGif from "../../assets/gif/OrderStatusPage.gif";
//@ts-ignore
import CheckoutPageGif from "../../assets/gif/CheckoutPage.gif";
import useConditions from "../../hooks/useConditions";
import { getSlotStatus } from "../../services/conditions.service";
import { Slot, SlotStatus } from "../../models";
import { useDispatch } from "react-redux";
import { updateSlot } from "../../app/slice/slotSlice";

type PageInfoType = {
  [key: string]: {
    pageName: string;
    pageTitle: string;
    activesteps: string[];
    activeGifURL: string;
  };
};

const pageInfo = (t: (key: string) => string, slotNo: number, slotName: string): PageInfoType => {
  let arrangedSlotName = "";

  if (slotNo <= 6) {
    arrangedSlotName = `Custom slot ${slotNo}`;
  } else {
    arrangedSlotName = slotName;
  }

  return {
    [PageType.THANK_YOU_PAGE]: {
      pageName: PageNames[PageType.THANK_YOU_PAGE] || "",
      pageTitle: t("Add Upsell.com to your Thank you page"),
      activesteps: [
        t(`(Or go to Settings ➝ Checkout ➝ Customize your checkout ➝ Thank you)`),
        `${t("Click")} <span style="font-size:14px;font-weight:500; line-height:16px">"Add app block"</span> ${t(
          "on the bottom left corner",
        )}`,
        `${t("Choose")} <span style="font-size:14px;font-weight:500; line-height:16px">Upsell.com ${arrangedSlotName}</span>`,
        t("Drag and drop the slots to locate them on the page"),
        `${t("Don't forget to hit")} <span style="font-size:14px;font-weight:500; line-height:16px">"Save"</span> ${t(
          "button",
        )}`,
      ],
      activeGifURL: ThankYouPageGif,
    },
    [PageType.ORDER_STATUS_PAGE]: {
      pageName: PageNames[PageType.ORDER_STATUS_PAGE] || "",
      pageTitle: t("Add Upsell.com to your Order status page"),
      activesteps: [
        t(`(Or go to Settings ➝ Checkout ➝ Customize your checkout ➝ Order status page)`),
        `${t("Click")} <span style="font-size:14px;font-weight:500; line-height:16px">"Add app block"</span> ${t(
          "on the bottom left corner",
        )}`,
        `${t("Choose")} <span style="font-size:14px;font-weight:500; line-height:16px">Upsell.com ${arrangedSlotName}</span>`,
        t("Drag and drop the slots to locate them on the page"),
        `${t("Don't forget to hit")} <span style="font-size:14px;font-weight:500; line-height:16px">"Save"</span> ${t(
          "button",
        )}`,
      ],
      activeGifURL: OrderStatusPageGif,
    },
    [PageType.CHECKOUT]: {
      pageName: PageNames[PageType.CHECKOUT] || "",
      pageTitle: t("Add Upsell.com to your checkout page"),
      activesteps: [
        `${t("(Or go to Settings ➝ Checkout ➝ Customize your checkout)")}`,
        `${t("Click")} <span style="font-size:14px;font-weight:500; line-height:16px">"Add app block"</span> ${t(
          "on the bottom left corner",
        )}`,
        `${t("Choose")} <span style="font-size:14px;font-weight:500; line-height:16px">Upsell.com ${arrangedSlotName}</span>`,
        t("Drag and drop the slots to locate them on the page"),
        `${t("Don't forget to hit")} <span style="font-size:14px;font-weight:500; line-height:16px">"Save"</span> ${t(
          "button",
        )}`,
      ],
      activeGifURL: CheckoutPageGif,
    },
  };
};

interface QuickSlotSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  slot: Slot;
}

const QuickSlotSetupModal = ({ isOpen, onClose, slot }: QuickSlotSetupModalProps) => {
  const { t } = useTranslation();
  const { activePage } = useAppSelector(selectAppState);
  const { selectedCondition } = useConditions();
  const { checkoutProfileId } = useAppSelector(selectAppState);
  const { success, error } = useToast();
  const dispatch = useDispatch();

  const onGoToEditor = useCallback(() => {
    let queryParams = "";

    if (activePage && ShopifyCheckoutEditorPage[activePage]) {
      queryParams = `?page=${ShopifyCheckoutEditorPage[activePage]}`;
    }

    if (activePage && ShopifyCheckoutEditorPageCollection[activePage]) {
      queryParams += `&collection=${ShopifyCheckoutEditorPageCollection[activePage]}`;
    }

    if (checkoutProfileId) {
      window.open(
        `https://admin.shopify.com/store/${selectedCondition?.platformStoreId?.replaceAll(".myshopify.com", "")}/settings/checkout/editor/profiles/${checkoutProfileId}${queryParams}&context=apps`,
      );
    } else {
      window.open(
        `https://${selectedCondition?.platformStoreId}/admin/settings/checkout/editor${queryParams}&context=extensibility&extensionPicker=true`,
        "_blank",
      );
    }
  }, [activePage, selectedCondition?.platformStoreId, checkoutProfileId]);

  const checkSlotStatus = useCallback(async () => {
    const response = await getSlotStatus(slot.slotNo, activePage!);

    if (response?.slotStatus !== SlotStatus.NEVER_DETECTED) {
      success(t("Slot status updated successfully"));
      dispatch(updateSlot({ id: slot.id, slotStatus: response?.slotStatus }));
    } else {
      error(t("Slot status not detected"));
    }
  }, [activePage, slot.id, slot.slotNo]);

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} closeOnEsc={true} size={"4xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Typography variant="h3-r-bold">
            {t(`Add Upsell.com slot to your ${activePage ? PageNames[activePage] : ""}`)}
          </Typography>
        </ModalHeader>
        <ModalCloseButton alignItems={"baseline"} w={0} h={0} />
        <ModalBody p={0} m={0}>
          <Divider />
          <Box p="24px">
            <Flex gap="24px">
              <Box width={"54%"}>
                <Flex alignItems={"center"} gap={3} mb={3}>
                  <Icons.Ellipse color={"primary.600"} />
                  <Box>
                    <Flex alignItems={"center"} gap={3}>
                      <Typography variant="small" fontWeight={400} color="neutrals.900">
                        {t("Go to your")}
                      </Typography>
                      <Button height={"auto"} p="8px 16px" rightIcon={<Icons.ExternalLink />} onClick={onGoToEditor}>
                        <Typography
                          variant="body"
                          sx={{
                            fontSize: "16px",
                          }}
                          fontWeight={500}
                        >
                          {activePage ? t(PageNames[activePage]!) : ""}
                        </Typography>
                      </Button>
                    </Flex>
                  </Box>
                </Flex>
                <Flex alignItems={"center"}>
                  <ListItem
                    title={activePage && pageInfo(t, slot.slotNo, slot.name)?.[activePage]?.activesteps?.[0]}
                    isFirstList={true}
                  />
                </Flex>
                {activePage &&
                  pageInfo(t, slot.slotNo, slot.name)
                    [activePage]?.activesteps?.slice(1)
                    .map((a: string, index: number) => {
                      return <ListItem title={a} isFirstList={false} key={index} />;
                    })}
              </Box>

              <Flex width={"50%"} justifyContent={"end"}>
                <img
                  src={activePage ? pageInfo(t, slot.slotNo, slot.name)[activePage]?.activeGifURL : ""}
                  alt="image_gif"
                  style={{
                    height: "200px",
                    width: "406px",
                  }}
                />
              </Flex>
            </Flex>
          </Box>
          <Divider />
        </ModalBody>
        <ModalFooter height={"36px"} p={0} m={"24px"}>
          <Flex justifyItems={"center"} justifyContent={"space-between"} w={"100%"}>
            <Flex gap="12px" alignItems="center" justifyContent={"center"}>
              <Typography variant="subheading2" fontWeight={500} color="#000000">
                {t(`Upsell.com on your ${activePage ? PageNames[activePage] : ""}`)} :
              </Typography>
              <Box
                borderRadius="16px"
                backgroundColor="warning.200"
                padding="2px 8px"
                display="flex"
                alignItems="center"
              >
                <Typography
                  color="warning.900"
                  variant="tiny"
                  fontWeight={500}
                  textTransform="uppercase"
                  letterSpacing="0.3px"
                >
                  {t("NOT DETECTED")}
                </Typography>
              </Box>
            </Flex>
            <Flex gap="8px">
              <Button
                height={"36px"}
                variant="ghost"
                onClick={() => {
                  onClose();
                }}
              >
                <Typography variant="subheading2" fontWeight={500} color="#000000">
                  {t("I'll do it later")}
                </Typography>
              </Button>
              <Button
                height={"36px"}
                variant="outline"
                onClick={() => {
                  checkSlotStatus();
                }}
              >
                <Typography variant="subheading2" fontWeight={500} color="#000000">
                  {t("Check status")}
                </Typography>
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface ListItemProps {
  title: string | undefined;
  isFirstList: boolean;
  key?: number;
}

const ListItem = ({ title, isFirstList }: ListItemProps) => {
  return (
    <>
      <Flex gap="12px" alignItems="center" justifyContent="flex-start" mt={isFirstList ? 0 : 3}>
        {!isFirstList && <Icons.Ellipse color={"primary.600"} />}
        {title && (
          <Typography
            variant="small"
            fontWeight={400}
            color="neutrals.900"
            dangerouslySetInnerHTML={{ __html: title }}
          ></Typography>
        )}
      </Flex>
    </>
  );
};

export default QuickSlotSetupModal;
